import axios from 'axios';
import { PAGINATION_LIMIT } from '../common/constants';
import { baseUrl, resolutionHeader } from '../Tenants';
import moment from 'moment';
import redirectToUpdate from './update';
import { IUnitAssignmentUpdate } from 'src/pages/Property/ManageStrataManagers/types';
import { handleSessionExpired } from 'src/services/auth/handleSessionExpired';
import {
  IDashboardFilters,
  IServiceProvider,
  IServiceProviderResponse,
  IServiceProvidersListParams,
} from 'src/services/api/ServiceRequestDashboard/apiContracts';
import {
  ICalendarEventsApiResponse,
  ICalendarEventsServiceResponse,
  ICalendarMetadataApiResult,
  ICalendarMetadataResponse,
  IKeyPickupEventApiResponse,
  IKeyPickupEventServiceResponse,
} from 'src/pages/Calendar/types';
import {
  ICalcomApiResult,
  ICalcomConflictingIntegration,
  ICalcomConnectionStatus,
  ICalcomEventType,
  ICalcomOAuthDetails,
  ICalcomOAuthExchangeRequest,
  ICalcomStartAuthorization,
  ICreatePropertyBookingLinkRequest,
  IPropertyBookingLink,
  IUpdatePropertyBookingLinkRequest,
  IUserAssociatedProperty,
  JuniperEventType,
  IUnitKeyPickupStatus,
  IKeyPickupSummary,
  IBulkInviteKeyPickupsRequest,
} from 'src/pages/Calendar/CalcomIntegration/types';
import {
  IContractFile,
  IContractFilesResponse,
  ISparkDeposit,
  ISparkDepositsResponse,
} from '../../public/APIContracts/APIContracts';
import {
  CsvDateFormat,
  IBulkKeyPickupResponse,
  IBulkWalkthroughRequest,
  IBulkWalkthroughResponse,
} from 'src/services/client-types';
import { BulkSuiteImportParams } from 'src/pages/Suite/Components/UnitImportDialogue/Hooks/BulkSaveSuiteAPITypes';
import { BulkKeyPickupPayloadToAPI } from 'src/components/SuitesTable/CustomHooks/useKeyPickups';
import {
  InventoryImportModel,
} from 'src/SharedCustomHooks/UseFetchInventoryUnitsForExport/types';
import {
  BulkActivateWalkthroughsPayload,
} from 'src/components/BulkOperations/Walkthroughs/BulkActivateWalkthroughs/Custom-Hooks/types';
import { HomeOwnerImportModel } from 'src/pages/Property/HomeOwnerImportDialogue/UseHomeownerImporter/types';
import { ServiceRequestCreationPayload } from 'src/components/ServiceRequestForm/ServiceRequestFormDetails/ServiceRequestFormDetailsCustomHooks/serviceRequestFormtypes';
import { IChangeContactEmailPayload } from 'src/pages/Contacts/apiContracts';

export const client = axios.create({
  baseURL: '/data/',
});

client.interceptors.request.use((c) => {
  c.url = c.url + '.json';
  return c;
});

type Method = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

interface IApiRequestData {
  method: Method;
  headers: {
    Accept: string;
    'Content-Type': string;
    'Tenant-Id': string;
    Authorization?: string;
  };
  body?: string;
}

export interface IApiError {
  error: string;
  isError: boolean;
}

type ApiResponse = Response | IApiError;

export const isApiError = (response: ApiResponse): response is IApiError => {
  if ((response as IApiError).isError === true) return true;
  if (response instanceof Response && (response.status < 200 || response.status > 299)) return true;
  return false;
};

export const apiReq = async (
  url: string,
  method: Method,
  needsAuthorization: boolean,
  objBody?: any,
  isLoginPage?: boolean,
  token?: string
): Promise<ApiResponse> => {
  // Configure request data.
  const data: IApiRequestData = {
    method,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'Tenant-Id': resolutionHeader,
    },
  };

  if (needsAuthorization) {
    try {
      const authToken: string =
        token ?? JSON.parse(localStorage.getItem('auth') ?? `""`);

      if (!authToken) {
        if (isLoginPage) {
          return { error: 'No authentication token found', isError: true };
        } else {
          await handleSessionExpired();
          return { error: 'No authentication token found', isError: true };
        }
      }

      data.headers.Authorization = 'Bearer ' + authToken;
    } catch (tokenError) {
      console.error('Token error:', tokenError);
      if (isLoginPage) {
        return { error: 'Authentication token error', isError: true };
      } else {
        await handleSessionExpired();
        return { error: 'Authentication token error', isError: true };
      }
    }
  }

  if (objBody !== null) {
    data.body = JSON.stringify(objBody);
  }

  try {
    const response: Response = await fetch(url, data);

    // Handle unauthorized access for login page.
    if (response.status === 401 && isLoginPage) {
      return response;
    }

    // Handle unauthorized access for non-login pages.
    // Skip logout redirect for offline/magic-link requests (explicit token passed in). todo: hack
    if (response.status === 401 && !isLoginPage && !token) {
      await handleSessionExpired();
      return { error: 'Unauthorized access', isError: true };
    }

    if (response.status === 401 && !isLoginPage && token) {
      return { error: 'Unauthorized access', isError: true };
    }

    if (response.status === 410) {
      redirectToUpdate();
      return { error: 'App is outdated', isError: true };
    }

    if (response.status == 400) {
      const body = await response.json();
      return {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        message: body.message,
        data: body.data,
        // legacy/workaround: preserve ASP.NET ProblemDetails so callers can
        // surface field-level validation instead of a bare "(400)".
        errors: body.errors,
        title: body.title,
      } as any;
    }

    return response;
  } catch (error: any) {
    console.error(`API request error for ${url}:`, error);
    return {
      error: error?.message || 'Request failed',
      isError: true,
    };
  }
};

export const createQueryParameters = (params) => {
  let query = '?';
  for (let key in params) {
    if (
      params[key] === null ||
      params[key] === undefined ||
      params[key].length === 0
    )
      continue;
    if (params[key] instanceof Array) {
      query += params[key].reduce((acc, value) => {
        return acc + key + '=' + value + '&';
      }, '');
    } else {
      query += key + '=' + params[key] + '&';
    }
  }
  return query;
};

export const signIn = async (data: { Username: string; Password: string }) => {
  return await apiReq(
    baseUrl + 'api/UserManagement/Login',
    'POST',
    false,
    data,
    true
  );
};

export const signIn2FA = async (twoFACode: string, twoFaToken: string) => {
  return await apiReq(
    baseUrl + 'api/UserManagement/login/2fa',
    'POST',
    true,
    { twoFACode },
    true,
    twoFaToken
  );
};

export const activateUser = (data) =>
  apiReq(baseUrl + 'api/UserManagement/ActivateUser', 'POST', true, data);

export const forgotPassword = (mailId) =>
  apiReq(
    baseUrl + 'api/UserManagement/ForgotPassword/' + mailId,
    'POST',
    false,
    null
  );

export const changePassword = (data) =>
  apiReq(baseUrl + 'api/UserManagement/ChangePassword', 'POST', true, data);

export const requestEmailChange = (newEmail: string) =>
  apiReq(baseUrl + 'api/usermanagement/RequestEmailChange', 'POST', true, { newEmail });

export const changeEmail = (payload: IChangeContactEmailPayload) =>
  apiReq(baseUrl + 'api/usermanagement/change-email', 'POST', true, payload);

export const confirmEmailChange = (code: string, email: string) =>
  apiReq(baseUrl + 'api/usermanagement/ConfirmEmailChange', 'POST', false, { code, email });

export const updateUserProfile = (data) =>
  apiReq(baseUrl + 'api/UserManagement/UpdateProfile', 'PUT', true, data);

//Assets API endpoints
export const getAssetsBySuiteId = (suiteId, userId, role) =>
  apiReq(
    baseUrl +
      'api/Properties/UnitAreaAssets/' +
      suiteId +
      '/' +
      userId +
      '/' +
      role,
    'GET',
    true,
    null
  );

export const addAsset = (data) =>
  apiReq(baseUrl + 'api/Properties/AddSuiteAsset', 'POST', true, data);
export const updateAsset = (data) =>
  apiReq(baseUrl + 'api/Properties/UpdateSuiteAsset', 'PUT', true, data);
export const deleteAsset = (id, userId) =>
  apiReq(
    baseUrl + 'api/Properties/Assets/' + id + '/' + userId,
    'DELETE',
    true,
    null
  );

//  Properties API end points
export const getProperties = (userId, role) =>
  apiReq(
    baseUrl +
      'api/Properties/PropertiesByUser/' +
      userId +
      '/' +
      role +
      '/Properties',
    'GET',
    true,
    null
  );
export const saveProperty = (data) =>
  apiReq(baseUrl + 'api/Properties/Properties', 'POST', true, data);
export const updateProperty = (data) =>
  apiReq(baseUrl + 'api/Properties/Properties', 'PUT', true, data);
export const deleteProperty = (id, userId) =>
  apiReq(
    baseUrl + 'api/Properties/Properties/' + id + '/' + userId,
    'DELETE',
    true,
    null
  );
export const getPropertyById = (id) =>
  apiReq(baseUrl + 'api/Properties/Properties/' + id, 'GET', true, null);
export const getPropertyByHomeownerId = (id) =>
  apiReq(
    baseUrl + 'api/Properties/HomeOwnerProperties/' + id,
    'GET',
    true,
    null
  );

export const triggerSparkSync = () =>
  apiReq(
    baseUrl + 'api/properties/spark-sync',
    'GET',
    true,
    null
  );

export const toggleSparkSyncSuspension = (propertyId: string, suspend: boolean) =>
  apiReq(
    baseUrl + `api/properties/${propertyId}/spark-sync/suspend/${suspend}`,
    'PATCH',
    true,
    null
  );

export const getSuitesByUser = (userId, role) =>
  apiReq(
    baseUrl + 'api/Properties/HomeOwnerSuiteList/' + userId + '/' + role,
    'GET',
    true,
    null
  );

export const getIndividualPropertySuiteByID = (propertyId, userId, role) =>
  apiReq(
    baseUrl +
      'api/Properties/UnitsbyProperty/' +
      propertyId +
      '/' +
      userId +
      '/' +
      role +
      '/Units',
    'GET',
    true,
    null
  );

export const getUnitsForBulkOperations = (propertyId: string) =>
  apiReq(
    `${baseUrl}api/Properties/UnitsForBulkOperations/${propertyId}`,
    'GET',
    true,
    null
  );

export const getPropertySuitesForTable = (id, userId, role) =>
  apiReq(
    baseUrl +
      'api/Properties/UnitListbyProperty/' +
      id +
      '/' +
      userId +
      '/' +
      role +
      '/Units',
    'GET',
    true,
    null
  );

export const getPropertySuiteById = (id) =>
  apiReq(baseUrl + 'api/Properties/Units/' + id, 'GET', true, null);

export const getUnitStrataDetails = (propertyId?: string) =>
  apiReq(
    baseUrl +
      'api/units/strata-managers' +
      (propertyId ? `?propertyId=${propertyId}` : ''),
    'GET',
    true,
    null
  );

export const getStrataManagers = () =>
  apiReq(baseUrl + 'api/usermanagement/strata-managers', 'GET', true, null);

export const putUnitStrataManagers = (payload: IUnitAssignmentUpdate[]) =>
  apiReq(baseUrl + 'api/units/strata-managers', 'PUT', true, payload);

export const getStrataManagerSuitesByProperty = () =>
  apiReq(baseUrl + 'api/units/strata-manager-units', 'GET', true, null);

export const getPropertiesSuites = (params) =>
  apiReq(baseUrl + 'api/Properties/UnitsbyPropertyList', 'POST', true, params);

export const updateSuiteSynchronization = (suiteId, toggle) =>
  apiReq(
    baseUrl +
      'api/properties/toggle-suite-synchronization/' +
      suiteId +
      '/' +
      toggle,
    'POST',
    true,
    null
  );

export const getTabList = () =>
  apiReq(baseUrl + 'api/Properties/Modules', 'GET', true, null);

export const getAllowedTabList = (id) =>
  apiReq(baseUrl + 'api/Properties/ModulePermissions/' + id, 'GET', true, null);

export const getModulePermissions = (propertyId: string, token?: string) =>
  apiReq(baseUrl + 'api/Properties/ModulePermissions/' + propertyId, 'GET', true, null, false, token);

export const getSuiteOnBoardingConveyanceFormListByPropertyCSV = (propertyId) =>
  apiReq(
    baseUrl +
      'api/Properties/SuiteOnBoardingConveyanceFormListByPropertyCSV/' +
      propertyId,
    'GET',
    true,
    null
  );

// Strips request params that a given dashboard endpoint must not receive.
// `dashboard-status-overview` and `dashboard-age-overview` count rows across
// status/age dimensions, so filtering by those same dimensions would zero
// out the buckets the UI is trying to render.
const excludeFiltersFromDashboardRequest = (
  filters: IDashboardFilters,
  paramsToExclude: string[]
): IDashboardFilters => {
  const filteredParams: Partial<IDashboardFilters> = {};

  for (const key in filters) {
    const filterIsOwnProperty: boolean = filters.hasOwnProperty(key);
    const filterIsNotExcluded: boolean = !paramsToExclude.includes(key);

    if (filterIsOwnProperty && filterIsNotExcluded) {
      filteredParams[key] = filters[key];
    }
  }

  return filteredParams as IDashboardFilters;
};

export const getDashboardServiceRequests = (filters: IDashboardFilters) =>
  apiReq(
    baseUrl +
      'api/ServiceRequest/dashboard-list' +
      createQueryParameters(filters),
    'GET',
    true,
    null,
    false
  );

export const getServiceRequestsByProperty = (propertyId: string) =>
  apiReq(
    baseUrl + `api/ServiceRequest/dashboard-list/by-property/${propertyId}`,
    'GET',
    true,
    null,
    false
  );

export const getServiceRequestsBySuite = (suiteId: string) =>
  apiReq(
    baseUrl + `api/ServiceRequest/dashboard-list/by-suite/${suiteId}`,
    'GET',
    true,
    null,
    false
  );

export const getServiceRequestsByContact = (contactId: string) =>
  apiReq(
    baseUrl + `api/ServiceRequest/dashboard-list/by-contact/${contactId}`,
    'GET',
    true,
    null,
    false
  );

export const getServiceRequestsByItem = (itemId: string) =>
  apiReq(
    baseUrl + `api/ServiceRequest/dashboard-list/by-item/${itemId}`,
    'GET',
    true,
    null,
    false
  );

export const getDashboardStatusOverview = (filters: IDashboardFilters) => {
  const filteredParams: IDashboardFilters = excludeFiltersFromDashboardRequest(
    filters,
    ['Statuses', 'ServiceRequestAge']
  );

  return apiReq(
    baseUrl +
      'api/ServiceRequest/dashboard-status-overview' +
      createQueryParameters(filteredParams),
    'GET',
    true,
    null,
    false
  );
};

export const getDashboardAgeOverview = (filters: IDashboardFilters) => {
  const filteredParams: IDashboardFilters = excludeFiltersFromDashboardRequest(
    filters,
    ['ServiceRequestAge']
  );

  return apiReq(
    baseUrl +
      'api/ServiceRequest/dashboard-age-overview' +
      createQueryParameters(filteredParams),
    'GET',
    true,
    null,
    false
  );
};

export const getServiceRequestByPropertyIdUserIdRole = (
  propertyId,
  userId,
  role,
  count,
  text
) =>
  apiReq(
    baseUrl +
      'api/ServiceRequest/ServiceRequestByPropertyPagination/' +
      propertyId +
      '/' +
      userId +
      '/' +
      role +
      '/' +
      count +
      '/' +
      PAGINATION_LIMIT +
      '/' +
      text,
    'GET',
    true,
    null
  );

export const getServiceRequestByCommonIdUserIdRole = (
  propertyId,
  role,
  count,
  text
) =>
  apiReq(
    baseUrl +
      'api/ServiceRequest/ServiceRequestByCommonArea/' +
      propertyId +
      '/' +
      role +
      '/' +
      count +
      '/' +
      PAGINATION_LIMIT +
      '/' +
      text,
    'GET',
    true,
    null
  );

//Suites API endpoints
export const addSuite = (suite) =>
  apiReq(baseUrl + 'api/Properties/Units', 'POST', true, suite);

export const updateSuite = (suite) =>
  apiReq(baseUrl + 'api/Properties/Units', 'PUT', true, suite);

export const deleteUnit = (id, userId) =>
  apiReq(
    baseUrl + 'api/Properties/Units/' + id + '/' + userId,
    'DELETE',
    true,
    null
  );

export const getSuiteById = (id) => client.get(`suites/${id}`);

export const getSuiteStatuses = () =>
  apiReq(baseUrl + 'api/Properties/SuiteStatuses', 'GET', true, null);

export const getSuiteAreas = () =>
  apiReq(baseUrl + 'api/Catalog/Areas', 'GET', true, null);

export const setWalkThroughStatus = (status) =>
  apiReq(
    baseUrl + 'api/Walkthrough/UpdateWalkThroughStatus',
    'PUT',
    true,
    status
  );

export const getContacts = async () => {
  try {
    const response: any = await apiReq(
      baseUrl + 'api/contacts/list',
      'GET',
      true,
      null
    );
    return response;
  } catch (error) {
    console.error('Error in getContacts:', error);
    throw error;
  }
};

export const getContactById = (contactId: string, suiteId?: string) => {
  const suiteSegment = suiteId ? '/' + suiteId : '';

  return apiReq(baseUrl + 'api/UserManagement/users/' + contactId + suiteSegment, 'GET', true, null);
};

export const saveContact = (data) =>
  apiReq(baseUrl + 'api/UserManagement/Users', 'POST', true, data);

export const sendInvitationToContact = (data) =>
  apiReq(
    baseUrl + 'api/UserManagement/SendActivateLinkToUsers',
    'POST',
    true,
    data
  );

export const generateActivationLink = (userId) =>
  apiReq(
    baseUrl + 'api/UserManagement/' + userId + '/activation-link',
    'POST',
    true,
    null
  );

export const updateContact = (data) =>
  apiReq(baseUrl + 'api/UserManagement/Users', 'PUT', true, data);

export const deleteContactById = (id) =>
  apiReq(
    baseUrl + 'api/UserManagement/Users/{' + id + '}',
    'DELETE',
    true,
    null
  );
export const getAllRoles = () =>
  apiReq(baseUrl + 'api/UserManagement/roles', 'GET', true, null);

export const changeUserRole = (userId: string, role: string) =>
  apiReq(baseUrl + 'api/UserManagement/change-role', 'PATCH', true, {
    userId,
    role,
  });

export const bulkUpdateAllowEmailChange = (userIds: string[], allow: boolean) =>
  apiReq(baseUrl + 'api/UserManagement/allow-email-change', 'PATCH', true, {
    userIds,
    allow,
  });

export const bulkUpdateServiceRequestVisibility = (
  serviceRequestIds: string[],
  externalVisibilityOverride: boolean
) =>
  apiReq(baseUrl + 'api/ServiceRequest/visibility', 'PATCH', true, {
    serviceRequestIds,
    externalVisibilityOverride,
  });

export const fetchAvailableServiceTimes = (id, servicePreferences) =>
  apiReq(
    baseUrl +
      'api/UserManagement/Users/' +
      id +
      '/' +
      moment().format('YYYY-MM-DD HH:mm:ss') +
      '/' +
      localStorage.getItem('theme') +
      '/AvailableServiceTimes/?serviceRequestPreferences=' +
      servicePreferences,
    'GET',
    true,
    null
  );

export const fetchPropertyDeveloperAvailableTimes = (id, servicePreferences) =>
  apiReq(
    baseUrl +
      'api/UserManagement/Users/' +
      id +
      '/' +
      moment().format('YYYY-MM-DD HH:mm:ss') +
      '/PropertyDeveloperAvailableTimes/?serviceRequestPreferences=' +
      servicePreferences,
    'GET',
    true,
    null
  );

export const createServiceRequest = (serviceRequest: ServiceRequestCreationPayload ) => apiReq(
    baseUrl + 'api/ServiceRequest',
    'POST',
    true,
    serviceRequest
  );
export const saveScheduledAppointment = (scheduledAppointment) =>
  apiReq(
    baseUrl + 'api/ServiceRequest/ScheduledAppointment',
    'POST',
    true,
    scheduledAppointment
  );

interface IUpdateServiceRequestStatusBody {
  name: string;
  LoginUserId?: string;
  TicketStatusId?: string;
}

export const saveStatus = (id, body: IUpdateServiceRequestStatusBody, token?: string) =>
  apiReq(baseUrl + 'api/ServiceRequest/' + id + '/Status', 'PATCH', true, body, false, token);

export const changeServiceRequestAsset = (serviceRequestId: string, assetId: string) =>
  apiReq(baseUrl + 'api/servicerequest/change-asset', 'PATCH', true, { serviceRequestId, assetId });

export const getServiceRequestById = (id, token?: string) =>
  apiReq(baseUrl + 'api/ServiceRequest/ServiceRequest/' + id, 'GET', true, null, false, token);

export const updateServiceRequestOneYearWarrantyIssueStatus = (data) =>
  apiReq(
    baseUrl + 'api/ServiceRequest/UpdateServiceRequestOneYearWarrantyIssue',
    'PUT',
    true,
    data
  );

export const getServiceRequestPdfById = (id) =>
  apiReq(
    baseUrl + 'api/ServiceRequest/ServiceRequestDetailsPDF/' + id,
    'GET',
    true,
    null
  );

export const fetchServiceRequestActivityById = (id, token?: string) =>
  apiReq(
    baseUrl + 'api/ServiceRequest/ServiceRequestActivityLog/' + id,
    'GET',
    true,
    null,
    false,
    token
  );
export const deleteServiceRequest = (id) =>
  apiReq(baseUrl + 'api/ServiceRequest/Delete/' + id, 'DELETE', true, null);

interface IServiceRequestSubscribersBody {
  userIds: string[];
}

export const getServiceRequestSubscribers = (
  serviceRequestId: string,
  token?: string
) =>
  apiReq(
    baseUrl + 'api/ServiceRequest/' + serviceRequestId + '/subscribers',
    'GET',
    true,
    null,
    false,
    token
  );

export const saveServiceRequestSubscribers = (
  serviceRequestId: string,
  body: IServiceRequestSubscribersBody,
  token?: string
) =>
  apiReq(
    baseUrl + 'api/ServiceRequest/' + serviceRequestId + '/subscribers',
    'PUT',
    true,
    body,
    false,
    token
  );

interface IPinServiceRequestResourceBody {
  resourceId: string;
}

export const getServiceRequestPinnedResource = (
  serviceRequestId: string,
  token?: string
) =>
  apiReq(
    baseUrl + 'api/ServiceRequest/' + serviceRequestId + '/pinned-resource',
    'GET',
    true,
    null,
    false,
    token
  );

export const pinServiceRequestResource = (
  serviceRequestId: string,
  body: IPinServiceRequestResourceBody,
  token?: string
) =>
  apiReq(
    baseUrl + 'api/ServiceRequest/' + serviceRequestId + '/pinned-resource',
    'PUT',
    true,
    body,
    false,
    token
  );

export const unpinServiceRequestResource = (
  serviceRequestId: string,
  token?: string
) =>
  apiReq(
    baseUrl + 'api/ServiceRequest/' + serviceRequestId + '/pinned-resource',
    'DELETE',
    true,
    null,
    false,
    token
  );

//Items API endpoints
export const getItems = (id, role) =>
  apiReq(baseUrl + 'api/Catalog/Items/' + id + '/' + role, 'GET', true, null);
export const getAllItemTypes = () =>
  apiReq(baseUrl + 'api/Catalog/ItemType', 'GET', true, null);
export const saveItem = (data) =>
  apiReq(baseUrl + 'api/Catalog/Items', 'POST', true, data);
export const deleteItem = (id) =>
  apiReq(baseUrl + 'api/Catalog/Items/' + id, 'DELETE', true, null);
export const updateItem = (data) =>
  apiReq(baseUrl + 'api/Catalog/Items', 'PUT', true, data);

// FAQS, NOTES, DOCUMENTS
export const updateFaqsNotesDocuments = (data: any, token?: string) =>
  apiReq(baseUrl + 'api/Resource/Resources', 'PUT', true, data, false, token);
export const deleteFaqsNotesDocuments = (id) =>
  apiReq(baseUrl + 'api/Resource/Resources/' + id, 'DELETE', true, null);
export const fetchFaqsNotesDocuments = (id) =>
  apiReq(baseUrl + `api/Resource/Resources/${id}`, 'GET', true, null);
export const fetchFaqsNotesDocumentForAssetByAssetId = (id, itemdata, token?) =>
  apiReq(
    baseUrl + 'api/Resource/AssetResourcesDetails/' + id + '/' + itemdata,
    'GET',
    true,
    null,
    false,
    token
  );
export const fetchServiceRequestFaqsNotesDocuments = (id, token?: string) =>
  apiReq(
    baseUrl + `api/Resource/ServiceRequestDocuments/${id}`,
    'GET',
    true,
    null,
    false,
    token
  );
export const addFaqsNotesDocuments = (data, token?: string) =>
  apiReq(baseUrl + 'api/Resource/Resources', 'POST', true, data, false, token);
export const getIssueTypes = () =>
  apiReq(baseUrl + 'api/Resource/IssueTypes', 'GET', true, null);
export const getTicketStatus = (token?: string) =>
  apiReq(baseUrl + 'api/Resource/TicketStatus', 'GET', true, null, false, token);
export const setStatusCustomLabel = (
  serviceRequestStatusId: string,
  customLabel: string,
  token?: string
) =>
  apiReq(
    baseUrl + 'api/ServiceRequest/status/custom-label',
    'POST',
    true,
    { serviceRequestStatusId, customLabel },
    false,
    token
  );
export const clearStatusCustomLabel = (id: string, token?: string) =>
  apiReq(
    baseUrl + `api/ServiceRequest/status/custom-label/${id}`,
    'DELETE',
    true,
    null,
    false,
    token
  );
export const getTypeaheadResults = (id, role, query) =>
  apiReq(
    baseUrl + 'api/common/ListFilter/' + id + '/' + role + '/' + query,
    'GET',
    true,
    null
  );
export const fetchMessages = (data) =>
  apiReq(baseUrl + 'api/Common/GetMessages', 'POST', true, data);
export const postMessage = (data, token?: string) =>
  apiReq(baseUrl + 'api/Common/SendMessage', 'POST', true, data, false, token);

export const sendEmailNotification = (data) =>
  apiReq(baseUrl + 'api/Common/SendEmailOnVideoChat', 'POST', true, data);

export const getUserProfileDetails = (userId) =>
  apiReq(
    baseUrl + 'api/UserManagement/GetSettings/' + userId,
    'GET',
    true,
    null
  );
export const updateCompanyDetails = (data) =>
  apiReq(
    baseUrl + 'api/UserManagement/UpdateCompanySettings',
    'PUT',
    true,
    data
  );
export const updateNotificationDetails = (data) =>
  apiReq(
    baseUrl + 'api/UserManagement/UpdateNotificationSettings',
    'PUT',
    true,
    data
  );

export const getSystemLevelNotifications = () =>
  apiReq(
    `${baseUrl}api/settings/system-level-notifications`,
    'GET',
    true,
    null
  );
export const updateSystemLevelNotification = (data) =>
  apiReq(
    `${baseUrl}api/settings/update-system-level-notification`,
    'PUT',
    true,
    data
  );

// property walkk through api's
export const saveConfigType = (data) =>
  apiReq(baseUrl + 'api/Walkthrough/Walkthrough', 'POST', true, data);

export const updateConfigType = (data) =>
  apiReq(baseUrl + 'api/Walkthrough/Walkthrough', 'PUT', true, data);

export const getAreasAndSubAreasBySuiteId = (walkthroughId) =>
  apiReq(
    baseUrl + 'api/Walkthrough/GetWalkthroughAreas/' + walkthroughId,
    'GET',
    true,
    null
  );

export const deleteSubArea = (subAreaId) => {
  throw new Error('Removed Api');
};

export const saveSubAreaDescription = (data) => {
  throw new Error('Removed Api');
};

export const getWalkthroughSuites = (propertyId) =>
  apiReq(
    baseUrl + 'api/Walkthrough/WalkthroughUnitsDrDown/' + propertyId,
    'GET',
    true,
    null
  );

export const saveCopyFromExisting = (data) =>
  apiReq(baseUrl + 'api/Walkthrough/CopyWalkthrough', 'POST', true, data);

export const fetchAreaData = (walkthroughId, areaId) =>
  apiReq(
    baseUrl + 'api/Walkthrough/WalkThroughArea/' + walkthroughId + '/' + areaId,
    'GET',
    true,
    null
  );

export const saveAreaDescription = (data) =>
  apiReq(baseUrl + 'api/Walkthrough/WalkThroughArea', 'PUT', true, data);

export const getWalkthroughDataByWalkthroughId = (walkthroughId) =>
  apiReq(
    baseUrl + 'api/Walkthrough/Walkthrough/' + walkthroughId,
    'GET',
    true,
    null
  );

export const fetchSuiteCheckList = (walkthroughId) =>
  apiReq(
    baseUrl +
      'api/Walkthrough/WalkthroughAreasChecklist/{' +
      walkthroughId +
      '}',
    'GET',
    true,
    null
  );

export const getAreaAndSubAreaStatus = (walkthroughId) => {
  throw new Error('Removed Api');
};

export const fetchLatestMessages = (suiteId) =>
  apiReq(
    baseUrl + `api/Common/GetMessages?$top=5&$orderBy=sentOn desc`,
    'POST',
    true,
    {
      referenceId: suiteId,
      referenceType: 'Suite',
    }
  );

export const fetchLatestFaqsNotesDocuments = (suiteId) =>
  apiReq(
    baseUrl +
      'api/Resource/DashboardResourcesBySuite/' +
      suiteId +
      '?$filter=type eq ' +
      "'DOCUMENTS'" +
      '&$orderBy=createdOn desc&$top=5',
    'GET',
    true,
    null
  );

export const fetchMaintenanceGuide = (suiteId) =>
  apiReq(
    baseUrl +
      'api/Resource/DashboardMaintenanceGuideBySuite/' +
      suiteId +
      '?$top=5',
    'GET',
    true,
    null
  );

export const fetchLatestServiceRequests = (suiteId) =>
  apiReq(
    baseUrl +
      'api/ServiceRequest/ServiceRequestListBySuite/' +
      suiteId +
      '?$top=5&$orderBy=createdDate desc',
    'GET',
    true,
    null
  );

export const fetchPropertyManagerDetailsForHomeowner = (propertyId) =>
  apiReq(
    baseUrl +
      'api/Properties/GetPropertyManagerDetailsForHomeOwner/' +
      propertyId,
    'GET',
    true,
    null
  );

export const fetchPropertyDetailsByProperty = (propertyId) =>
  apiReq(
    baseUrl + 'api/Properties/PropertyDetailsByProperty/' + propertyId,
    'GET',
    true,
    null
  );

export const fetchWarrantyStatus = (suiteId) =>
  apiReq(
    baseUrl + 'api/Properties/AssetsWarrantExpirationBySuite/' + suiteId,
    'GET',
    true,
    null
  );

export const getRecentActivity = (suiteId) =>
  apiReq(
    baseUrl + 'api/ServiceRequest/ServiceRequestActivitysBySuite/' + suiteId,
    'GET',
    true,
    null
  );

export const fetchCalendlyLink = (email) =>
  apiReq(
    baseUrl + 'api/UserManagement/UserCalendarLink/' + email,
    'GET',
    true,
    null
  );

export const getLocations = (value) =>
  apiReq(baseUrl + 'api/Common/GetGoogleLocations/' + value, 'GET', true, null);

export const fetchWalkThroughServiceList = (walkthroughId) =>
  apiReq(
    baseUrl +
      'api/Walkthrough/GetWalkthroughServiceRequestlistData/' +
      walkthroughId,
    'GET',
    true,
    null
  );

export const updateWalkThroughRoomSequence = (params) =>
  apiReq(
    baseUrl + 'api/Walkthrough/UpdateWalkThroughRoomSequence',
    'PUT',
    true,
    params
  );

export const fetchServiceRequestsByAssetId = (assetId, walkthroughId) =>
  apiReq(
    baseUrl +
      'api/ServiceRequest/GetServiceRequestByAsset/' +
      assetId +
      '/' +
      walkthroughId,
    'GET',
    true,
    null
  );

export const saveAnnouncement = (params) =>
  apiReq(baseUrl + 'api/Common/Announcement', 'POST', true, params);

export const getAnnouncements = (id, role) =>
  apiReq(
    baseUrl + 'api/Common/Announcement/' + id + '/' + role,
    'GET',
    true,
    null
  );

export const getAnnouncementById = (id) =>
  apiReq(baseUrl + 'api/Common/AnnouncementById/' + id, 'GET', true, null);

export const getLatestAnnouncements = (suiteId) =>
  apiReq(
    baseUrl +
      'api/Common/AnnouncementsBySuite/' +
      suiteId +
      '?$orderby=announcementDate desc&&$top=2',
    'GET',
    true,
    null
  );

export const deleteAnnouncement = (id) =>
  apiReq(baseUrl + 'api/Common/Announcement/' + id, 'DELETE', true, null);

export const getAnnouncementBuildings = (id) =>
  apiReq(baseUrl + 'api/Properties/PropertyBuildings/' + id, 'GET', true, null);
export const getAnnouncementLevels = (id) =>
  apiReq(
    baseUrl + 'api/Properties/BuildingLevelsProperty/' + id,
    'GET',
    true,
    null
  );

export const getAnnouncementUnits = (params) =>
  apiReq(
    baseUrl + 'api/Common/AnnouncementBuildingLevels',
    'POST',
    true,
    params
  );

export const fetchSuiteWalkThroughs = (suiteId, roleName) =>
  apiReq(
    baseUrl + 'api/Walkthrough/WalkthroughbySuite/' + suiteId + '/' + roleName,
    'GET',
    true,
    null
  );

export const fetchCommonAreas = () =>
  apiReq(baseUrl + 'api/Properties/CommonAreas', 'GET', true, null);

export const savePropertyCommonAreas = (data) =>
  apiReq(baseUrl + 'api/Properties/PropertyCommonAreas', 'PUT', true, data);

export const fetchPropertyCommonAreas = (propertyId) =>
  apiReq(
    baseUrl + 'api/Properties/PropertyCommonAreas/' + propertyId,
    'GET',
    true,
    null
  );

export const fetchSuiteUsers = (suiteId) =>
  apiReq(baseUrl + 'api/Common/UsersListBySuite/' + suiteId, 'GET', true, null);

export const updateServiceRequest = (serviceRequest) =>
  apiReq(
    baseUrl + 'api/ServiceRequest/ServiceRequest',
    'PUT',
    true,
    serviceRequest
  );

export const createServiceRequestByGroup = (params) =>
  apiReq(
    baseUrl + 'api/ServiceRequest/GroupingServiceRequests',
    'POST',
    true,
    params
  );

export const deleteServiceRequestGroup = (groupingServiceRequestId, userId) =>
  apiReq(
    baseUrl +
      'api/ServiceRequest/DeleteGroupingServiceRequest/' +
      groupingServiceRequestId +
      '/' +
      userId,
    'DELETE',
    true,
    null
  );

export const deleteServiceRequestFromGroup = (serviceRequestId, usersId) =>
  apiReq(
    baseUrl +
      'api/ServiceRequest/DeleteServiceRequestFromGrouping/' +
      serviceRequestId +
      '/' +
      usersId,
    'DELETE',
    true,
    null
  );

export const fetchEmailTemplates = () =>
  apiReq(baseUrl + 'api/Common/HtmlEmailTemplateList', 'GET', true, null);

export const updateEmailTemplate = (params) =>
  apiReq(baseUrl + 'api/Common/HtmlEmailTemplate', 'PUT', true, params);

export const fetchServicesProviderAndPropertyDeveloperList = (
  LoginUserId,
  Role
) =>
  apiReq(
    baseUrl +
      'api/ServiceRequest/ServicesProvidedandPropertyDeveloperList/' +
      LoginUserId +
      '/' +
      Role,
    'GET',
    true,
    null
  );

export const getfetchCommonIssues = (assetId) =>
  apiReq(
    baseUrl + 'api/Common/CommonIssuesByAsset/' + assetId,
    'GET',
    true,
    null
  );

export const saveCommonIssue = (params) =>
  apiReq(baseUrl + 'api/Common/CommonIssues', 'POST', true, params);

export const updateCommonIssue = (params) =>
  apiReq(baseUrl + 'api/Common/CommonIssues', 'PUT', true, params);

export const deleteCommonIssue = (id) =>
  apiReq(baseUrl + 'api/Common/CommonIssues/' + id, 'DELETE', true, null);

export const fetchCommonAreaAssetsFromAPI = (commonAreaId) =>
  apiReq(
    baseUrl + 'api/Properties/CommonArea/' + commonAreaId + '/Assets',
    'GET',
    true,
    null
  );

export const saveCommonAreaAsset = (params) =>
  apiReq(baseUrl + 'api/Properties/CommonAreaAsset', 'POST', true, params);

export const updateCommonAreaAsset = (params) =>
  apiReq(baseUrl + 'api/Properties/CommonAreaAsset', 'PUT', true, params);

export const deleteCommonAreaAsset = (commonAreaAssetId) =>
  apiReq(
    baseUrl + 'api/Properties/CommonAreaAsset/' + commonAreaAssetId,
    'DELETE',
    true,
    null
  );

export const fetchPropertyManagersByPropertyId = (propertyId) =>
  apiReq(
    baseUrl + 'api/Properties/PropertyManagerDetailsByProperty/' + propertyId,
    'GET',
    true,
    null
  );

export const fetchPropertyCounts = (propertyId, userId, role) =>
  apiReq(
    baseUrl +
      'api/Properties/CountsByProperty/' +
      propertyId +
      '/' +
      userId +
      '/' +
      role,
    'GET',
    true,
    null
  );

export const fetchSuiteCounts = (suiteId, role) =>
  apiReq(
    baseUrl + 'api/Properties/CountsBySuite/' + suiteId + '/' + role,
    'GET',
    true,
    null
  );

export const addExistingPropertyDeveloper = (data) =>
  apiReq(
    baseUrl + 'api/Properties/CopyPropertyManagerToProperty',
    'POST',
    true,
    data
  );

export const fetchContactCounts = (
  contactId,
  role,
  loginUserId,
  loginUserRole
) =>
  apiReq(
    baseUrl +
      'api/Properties/CountsByUser/' +
      contactId +
      '/' +
      role +
      '/' +
      loginUserId +
      '/' +
      loginUserRole,
    'GET',
    true,
    null
  );

export const updateBulkStatus = (params) =>
  apiReq(
    baseUrl + 'api/ServiceRequest/UpdateBulkServiceRequestStatus',
    'PUT',
    true,
    params
  );

export const fetchReports = (params) =>
  apiReq(
    baseUrl + 'api/ServiceRequest/ServiceRequestListByProperties',
    'POST',
    true,
    params
  );

export const fetchUserActivityReport = (params) =>
  apiReq(
    baseUrl + 'api/UserManagement/GetUserActivityLogs',
    'POST',
    true,
    params
  );

export const fetchUserStatusReports = (propertyIds) =>
  apiReq(
    baseUrl + 'api/common/UserStatusReportList/' + propertyIds,
    'GET',
    true,
    null
  );

export const fetchServiceRequestStatsReport = (data) =>
  apiReq(
    baseUrl + 'api/report/service-requests-stats' + createQueryParameters(data),
    'GET',
    true,
    null
  );

export const deletePropertyDeveloper = (id, propertyId) =>
  apiReq(
    baseUrl +
      `api/Properties/DeletePropertyManagerFromProperty/${propertyId}/${id}`,
    'DELETE',
    true,
    null
  );

export const validateUser = (params) =>
  apiReq(baseUrl + 'api/UserManagement/UserVerification', 'PUT', false, params);

export const fetchSelectedServiceRequestCSVExportDownloadList = (
  serviceRequestIds: string[],
  dateFormat: CsvDateFormat
) =>
  apiReq(
    baseUrl + 'api/ServiceRequest/ServiceRequestSelectedListForCSV/',
    'POST',
    true,
    { serviceRequestIds, dateFormat }
  );

export const fetchSelectedServiceRequestPDFExportDownloadList = (id, params) =>
  apiReq(
    baseUrl +
      'api/ServiceRequest/ServiceRequestSelectedListForPDF/' +
      params +
      '/' +
      id,
    'GET',
    true,
    null
  );
export const fetchMainServiceRequestPDFDownloadList = (id) =>
  apiReq(
    baseUrl + 'api/ServiceRequest/UserPDFRequests/' + id,
    'GET',
    true,
    null
  );

export const onFetchLatestVideos = (suiteId) =>
  apiReq(
    baseUrl +
      'api/Resource/HomeownerEmbedVideosBySuite/' +
      suiteId +
      '?orderBy=createdOn desc',
    'GET',
    true,
    null
  );

export const downloadPDF = (walkthroughId) =>
  apiReq(
    baseUrl + 'api/Walkthrough/WalkthroughReviewPDFURL/' + walkthroughId,
    'GET',
    true,
    null
  );

export const fetchMaintainanceGuideBySuiteId = (id) =>
  apiReq(
    baseUrl + 'api/Properties/AssetsMaintenanceGuidesBySuite/' + id,
    'GET',
    true,
    null
  );

export const getMaintenanceGuidesByItemId = (itemId, source = 'Catalog') =>
  apiReq(
    baseUrl + `api/Properties/MaintenanceGuides/${itemId}?source=${source}`,
    'GET',
    true
  );

export const getPropertyBuildings = (propertyId) =>
  apiReq(
    baseUrl + 'api/Properties/PropertyBuildings/' + propertyId,
    'GET',
    true,
    null
  );

export const getBuildingLevels = (buildingId) =>
  apiReq(
    baseUrl + 'api/Properties/BuildingLevels/' + buildingId,
    'GET',
    true,
    null
  );

export const addExistingHomeownerToSuite = (data) =>
  apiReq(
    baseUrl + 'api/Properties/HomeownerAssociateToSuite',
    'PUT',
    true,
    data
  );

export const CheckExistingParkingStallStorageLockers = (
  propertyId,
  name,
  type
) =>
  apiReq(
    `${baseUrl}api/Properties/CheckExistingParkingStallStorageLockers/${propertyId}/${name}/${type}`,
    'GET',
    true,
    null
  );

export const getServiceProviders = async (
  params: IServiceProvidersListParams
): Promise<IServiceProvider[]> => {
  try {
    const response: any = await apiReq(
      baseUrl + 'api/ServiceRequest/ServicesProvidedListByServiceType',
      'POST',
      true,
      params
    );

    if (!response.ok) {
      const errorMessage = `Failed to fetch service providers: ${response.status} ${response.statusText}`;
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error('Unexpected error in getServiceProviders:', error);
    throw error;
  }
};

export const getServiceProvidersFilteredByPropertyId = async (
  propertyId: string
): Promise<IServiceProviderResponse> => {
  try {
    const url: string =
      baseUrl + 'api/usermanagement/service-providers?propertyId=' + propertyId;
    const response: any = await apiReq(url, 'GET', true, null);

    if (!response.ok) {
      const errorMessage = `Failed to fetch service providers: ${response.status} ${response.statusText}`;
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error(
      'Unexpected error in getServiceProvidersFilteredByPropertyId:',
      error
    );
    throw error;
  }
};

export const onSubmitKeyhandOffProcess = (params) =>
  apiReq(baseUrl + 'api/v2/Properties/KeyFobsSignatures', 'POST', true, params);

export const reRenderKeyPickupPdf = (suiteId) =>
  apiReq(
    baseUrl + `api/properties/re-render-key-pickup-pdf/${suiteId ?? ''}`,
    'GET',
    true,
    null
  );

export const onWalkthroughScheduled = (params) =>
  apiReq(
    baseUrl + 'api/Walkthrough/WalkThroughReviewSchedule',
    'POST',
    true,
    params
  );

export const saveHomeOwnerOnboarding = (params) =>
  apiReq(
    baseUrl + 'api/Properties/SuiteOnBoarding',
    params && params.id ? 'PUT' : 'POST',
    true,
    params
  );

export const saveNotaryForm = (params) =>
  apiReq(
    baseUrl + 'api/Properties/SuiteOnBoardingConveyanceForm',
    params && params.id ? 'PUT' : 'POST',
    true,
    params
  );

export const fetchHomeOwnerOnboarding = (propertyId) =>
  apiReq(
    baseUrl + 'api/Properties/SuiteOnBoardingList/' + propertyId,
    'GET',
    true,
    null
  );

export const changeOrdersSequence = (params) =>
  apiReq(baseUrl + 'api/Properties/SuiteOnBoardingOrder', 'PUT', true, params);

export const getPropertyStages = () =>
  apiReq(baseUrl + 'api/Properties/ConstructionStages', 'GET', true, null);

export const getPropertyStatuses = () =>
  apiReq(baseUrl + 'api/Properties/PropertyStatuses', 'GET', true, null);
export const updateSuiteOnBoardingFAQsOrder = (params) =>
  apiReq(
    baseUrl + 'api/Properties/UpdateSuiteOnBoardingFAQsOrder',
    'PUT',
    true,
    params
  );

export const updateSuiteOnBoardingChecklistsOrder = (params) =>
  apiReq(
    baseUrl + 'api/Properties/UpdateSuiteOnBoardingChecklistOrder',
    'PUT',
    true,
    params
  );

export const updateSuiteOnBoardingKeysOrder = (params) =>
  apiReq(
    baseUrl + 'api/Properties/UpdateSuiteOnBoardingKeyInformationOrder',
    'PUT',
    true,
    params
  );

export const updateSuiteOnBoardingOffersOrder = (params) =>
  apiReq(
    baseUrl + 'api/Properties/UpdateSuiteOnBoardingOffersOrder',
    'PUT',
    true,
    params
  );
export const fetchPropertyStages = (propertyId) =>
  apiReq(
    baseUrl + 'api/Properties/PropertyConstructionStages/' + propertyId,
    'GET',
    true,
    null
  );

export const onFetchMoreDetails = (id) =>
  apiReq(baseUrl + 'api/Properties/UnitSparkDetails/' + id, 'GET', true, null);

export const onFetchContactMoreDetails = (id) =>
  apiReq(
    baseUrl + 'api/UserManagement/UserSparkDetails/' + id,
    'GET',
    true,
    null
  );

export const fetchOnboardingOrders = (propertyId) =>
  apiReq(
    baseUrl + 'api/Properties/SuiteOnBoardings/' + propertyId,
    'GET',
    true,
    null
  );

export const fetchOnboardingOrderDetails = (
  propertyId,
  orderId,
  suiteId,
  userId
) =>
  apiReq(
    baseUrl +
      'api/Properties/SuiteOnBoarding/' +
      propertyId +
      '/' +
      orderId +
      '/' +
      suiteId +
      '/' +
      userId,
    'GET',
    true,
    null
  );

export const onChangeChecklistStatus = (params) =>
  apiReq(
    baseUrl + 'api/Properties/SuiteOnBoardingChecklist',
    'PUT',
    true,
    params
  );

export const onAddChecklistNote = (params) =>
  apiReq(
    baseUrl + 'api/Properties/SuiteOnboardingChecklistNote',
    'POST',
    true,
    params
  );

export const onAddChecklistDocument = (params) =>
  apiReq(
    baseUrl + 'api/Properties/SuiteOnboardingChecklistDocuments',
    'PUT',
    true,
    params
  );

export const onUpdateChecklistNote = (params) =>
  apiReq(
    baseUrl + 'api/Properties/SuiteOnboardingChecklistNote',
    'PUT',
    true,
    params
  );

export const onDeleteOrder = (orderId, userId) =>
  apiReq(
    baseUrl + `api/Properties/DeleteSuiteOnBoarding/${orderId}/${userId}`,
    'DELETE',
    true,
    null
  );

export const fetchPropertyCatalog = (propertyId) =>
  apiReq(
    baseUrl + `api/Catalog/ItemsByProperty/${propertyId}`,
    'GET',
    true,
    null
  );

export const fetchKeyContacts = (propertyId) =>
  apiReq(
    baseUrl + 'api/Common/KeyContactsListByProperty/' + propertyId,
    'GET',
    true,
    null
  );

export const deleteOnboardingDocument = (id, userId) =>
  apiReq(
    baseUrl +
      'api/Properties/DeleteSuiteOnboardingChecklistDocument/' +
      id +
      '/' +
      userId,
    'DELETE',
    true,
    null
  );

// inbox APIs
export const fetchInboxList = (userId, roleName, search) =>
  apiReq(
    baseUrl +
      'api/Common/ServiceRequestAndSuiteMessages/' +
      userId +
      '/' +
      roleName +
      '/' +
      search,
    'GET',
    true,
    null
  );
export const fetchAnnouncementList = (userId, roleName, search) =>
  apiReq(
    baseUrl +
      'api/Common/InboxAnnouncementList/' +
      userId +
      '/' +
      roleName +
      '/' +
      search,
    'GET',
    true,
    null
  );

export const fetchInboxSuitesList = (userId, roleName, search) =>
  apiReq(
    baseUrl +
      'api/Properties/UnitListbyUser/' +
      userId +
      '/' +
      roleName +
      '/' +
      search,
    'GET',
    true,
    null
  );

export const fetchChatMessages = (referenceId, referenceType, token?: string) =>
  apiReq(
    baseUrl +
      'api/Common/ServiceRequestAndSuiteMessageDetails/' +
      referenceId +
      '/' +
      referenceType,
    'GET',
    true,
    null,
    false,
    token
  );

export const fetchChatUserInformation = (referenceId, referenceType) =>
  apiReq(
    baseUrl +
      'api/Common/MessagingusersInfo/' +
      referenceId +
      '/' +
      referenceType,
    'GET',
    true,
    null
  );

export const fetchInboxAnnouncementsByProperty = (
  userId,
  roleName,
  propertyId
) =>
  apiReq(
    baseUrl +
      'api/Common/InboxAnnouncementDetailsByProperty/' +
      userId +
      '/' +
      roleName +
      '/' +
      propertyId,
    'GET',
    true,
    null
  );

export const fetchInboxServiceRequestList = (
  userId,
  roleName,
  pageNumber,
  search
) =>
  apiReq(
    baseUrl +
      'api/ServiceRequest/ServiceRequestsServiceRequestListforMessages/' +
      userId +
      '/' +
      roleName +
      '/' +
      pageNumber +
      '/' +
      PAGINATION_LIMIT +
      '/' +
      search,
    'GET',
    true,
    null
  );

export const deleteWalkthrough = (walkThroughId, userId) =>
  apiReq(
    baseUrl + `api/Walkthrough/Walkthrough/${walkThroughId}/${userId}`,
    'DELETE',
    true,
    null
  );

export const addBulkSuites = (params: BulkSuiteImportParams) =>
  apiReq(baseUrl + 'api/v2/properties/suiteImport', 'POST', true, params);

export const addBulkCatalogs = (params) =>
  apiReq(baseUrl + 'api/Catalog/ImportItems', 'POST', true, params);

export const fetchSuiteFaqs = (suiteId) =>
  apiReq(
    baseUrl + 'api/Resource/FaqsListBysuite/' + suiteId,
    'GET',
    true,
    null
  );

export const fetchSuiteTopFaqs = (suiteId) =>
  apiReq(
    baseUrl + 'api/Resource/FaqsListBysuite/' + suiteId + '?$top=5',
    'GET',
    true,
    null
  );

export const fetchInboxUnreadCount = () =>
  apiReq(baseUrl + 'api/Common/inbox/unread-count', 'GET', true, null);

// New mark-read shape: no /{userId} in the route, response carries the
// recomputed unreadCount. Kept alongside the legacy updateUnreadToReadMessages
// until the InboxDashboard/InboxLink unwiring phase removes the old path.
export const markThreadRead = (referenceId, referenceType) =>
  apiReq(
    baseUrl +
      'api/Common/UpdateMessageReadStatus/' +
      referenceId +
      '/' +
      referenceType,
    'POST',
    true,
    null
  );

export const validateURL = (email, code) =>
  apiReq(
    baseUrl +
      'api/UserManagement/PasswordLinkVerification/' +
      email +
      '/' +
      code,
    'GET',
    false,
    null
  );

export const fetchSpecifications = (itemId) =>
  apiReq(
    baseUrl + 'api/Catalog/SpecificationsListbyCatalog/' + itemId,
    'GET',
    true,
    null
  );

export const saveItemSpecifications = (params) =>
  apiReq(baseUrl + 'api/Catalog/CatalogSpecifications', 'POST', true, params);

export const updateItemSpecifications = (params) =>
  apiReq(baseUrl + 'api/Catalog/CatalogSpecifications', 'PUT', true, params);

export const deleteItemSpecifications = (specificationId) =>
  apiReq(
    baseUrl + 'api/Catalog/CatalogSpecifications/' + specificationId,
    'DELETE',
    true,
    null
  );

export const fetchkeypickupServiceRequests = (suiteId) =>
  apiReq(
    baseUrl +
      'api/ServiceRequest/WalkthroughServiceRequestListForKeyPickup/' +
      suiteId,
    'GET',
    true,
    null
  );

export const fetchkeypickupImportantDocuments = (suiteId) =>
  apiReq(
    baseUrl + 'api/Resource/SuiteImportantDocuments/' + suiteId,
    'GET',
    true,
    null
  );

export const getServiceRequestTags = () =>
  apiReq(baseUrl + 'api/ServiceRequest/Tags', 'GET', true, null);
export const saveCustomerFeedback = (params) =>
  apiReq(baseUrl + 'api/Common/SaveCustomerFeedback', 'POST', true, params);
export const fetchCustomerFeedback = (params) =>
  apiReq(baseUrl + 'api/Common/GetCustomerFeedback', 'POST', true, params);
export const saveServiceLog = (params) =>
  apiReq(
    baseUrl + 'api/ServiceRequest/UpdateServiceRequestEstimatedTime',
    'PUT',
    true,
    params
  );
export const ValidateCustomerFeedback = (ServiceRequestId) =>
  apiReq(
    baseUrl + 'api/common/ValidateCustomerFeedback/' + ServiceRequestId,
    'GET',
    true,
    null
  );
export const UpdateUserPDFName = (UserId, Id, Name) =>
  apiReq(
    baseUrl +
      'api/ServiceRequest/UpdateUserPDFName/' +
      UserId +
      '/' +
      Id +
      '/' +
      Name,
    'GET',
    true,
    null
  );
export const DeleteUserPDFRequest = (UserId, Id) =>
  apiReq(
    baseUrl + 'api/ServiceRequest/DeleteUserPDFRequest/' + UserId + '/' + Id,
    'GET',
    true,
    null
  );

export const saveArea = async (data) => {
  console.warn(
    'saveArea function is missing from client.js - this is a placeholder'
  );
  return { success: false, message: 'Not implemented' };
};

export const getServiceProviderProperties = (serviceProviderUserId) =>
  apiReq(
    baseUrl +
      `api/usermanagement/service-provider-properties/${serviceProviderUserId}`,
    'GET',
    true,
    null
  );

export const fetchWalkthroughVsWarrantyReport = (
  listOfSelectedPropertyIds?: string[]
) => {
  let url: string = `${baseUrl}api/report/walkthrough-vs-warranty-stats`;

  // If we have property IDs in the array, append them as parameters
  if (listOfSelectedPropertyIds && listOfSelectedPropertyIds.length > 0) {
    // Create parameter string from all property IDs.
    const propertyParams: string = listOfSelectedPropertyIds
      .map((id: string) => `propertyId=${id}`)
      .join('&');

    // Append parameters to URL.
    url += `?${propertyParams}`;
  }

  return apiReq(url, 'GET', true, null);
};

export const getCalendarEvents = async (
  from: string,
  to: string
): Promise<ICalendarEventsServiceResponse> => {
  const params = new URLSearchParams();
  if (from) params.append('from', from);
  if (to) params.append('to', to);

  const queryString: string = params.toString();
  const url: string =
    baseUrl + 'api/calendar/events' + (queryString ? `?${queryString}` : '');

  try {
    const response: any = await apiReq(url, 'GET', true, null);

    if (response && response.ok) {
      const data: ICalendarEventsApiResponse = await response.json();
      return { ok: true, data };
    }

    console.error('Calendar API error:', response);
    return {
      ok: false,
      error: response.error || 'Failed to fetch calendar events',
      status: response.status,
    };
  } catch (error: any) {
    console.error('Calendar API exception:', error);
    return {
      ok: false,
      error: error.message || 'Exception while fetching calendar events',
    };
  }
};

export const getKeyPickupEventBySuite = async (
  suiteId: string
): Promise<IKeyPickupEventServiceResponse> => {
  const url: string = baseUrl + `api/calendar/events/key-pickup/${suiteId}`;

  try {
    const response: any = await apiReq(url, 'GET', true, null);

    if (response && response.ok) {
      const data: IKeyPickupEventApiResponse = await response.json();
      return { ok: true, data };
    }

    // 404 = this suite has no key-pickup event; a legitimate empty result, not an error.
    if (response && response.status === 404) {
      return { ok: false, status: 404 };
    }

    console.error('Key pickup event API error:', response);
    return {
      ok: false,
      error: response.error || 'Failed to fetch key pickup event',
      status: response.status,
    };
  } catch (error: any) {
    console.error('Key pickup event API exception:', error);
    return {
      ok: false,
      error: error.message || 'Exception while fetching key pickup event',
    };
  }
};

export const getCalendarMetadata =
  async (): Promise<ICalendarMetadataApiResult> => {
    const url: string = baseUrl + 'api/calendar/metadata';

    try {
      const response: any = await apiReq(url, 'GET', true, null);

      if (response && response.ok) {
        const data: ICalendarMetadataResponse = await response.json();
        return { ok: true, data };
      }

      console.error('Calendar Metadata API error:', response);
      return {
        ok: false,
        error: response.error || 'Failed to fetch calendar metadata',
        status: response.status,
      };
    } catch (error: any) {
      console.error('Calendar Metadata API exception:', error);
      return {
        ok: false,
        error: error.message || 'Exception while fetching calendar metadata',
      };
    }
  };

/* Full-page redirect connect: the backend builds the Cal.com authorize URL with a single backend
 * callback as redirect_uri (so no per-tenant redirect-URI registration). The SPA navigates to it;
 * Cal.com bounces back through that callback to `returnOrigin` with ?calcom=connected|error. */
export const connectCalcomFullPageRedirect = async (
  returnOrigin: string
): Promise<ICalcomApiResult<ICalcomStartAuthorization>> => {
  try {
    const response: any = await apiReq(
      `${baseUrl}api/calendar/calcom/oauth/connect?returnOrigin=${encodeURIComponent(
        returnOrigin
      )}`,
      'GET',
      true,
      null
    );

    if (response && response.ok) {
      const body = await response.json();
      return { ok: true, data: body.data };
    }

    return {
      ok: false,
      error:
        response?.message ||
        response?.error ||
        'Failed to start Cal.com connection',
      status: response?.status,
    };
  } catch (error: any) {
    console.error('Cal.com OAuth connect request failed:', error);
    return {
      ok: false,
      error: error?.message || 'Failed to start Cal.com connection',
    };
  }
};

// Source of truth for connect/disconnect UI. The SPA never holds Cal.com tokens.
export const getCalcomConnectionStatus = async (): Promise<
  ICalcomApiResult<ICalcomConnectionStatus>
> => {
  try {
    const response: any = await apiReq(
      `${baseUrl}api/calendar/calcom/oauth/me`,
      'GET',
      true,
      null
    );

    if (response && response.ok) {
      const body = await response.json();
      return { ok: true, data: body.data };
    }

    return {
      ok: false,
      error:
        response?.message ||
        response?.error ||
        'Failed to fetch Cal.com connection status',
      status: response?.status,
    };
  } catch (error: any) {
    console.error('Cal.com OAuth status request failed:', error);
    return {
      ok: false,
      error: error?.message || 'Failed to fetch Cal.com connection status',
    };
  }
};

export const getCalcomConflictingIntegration = async (
  integrationId: string
): Promise<ICalcomApiResult<ICalcomConflictingIntegration>> => {
  try {
    const response: any = await apiReq(
      `${baseUrl}api/calendar/calcom/oauth/integration/${integrationId}`,
      'GET',
      true,
      null
    );

    if (response && response.ok) {
      const body = await response.json();
      return { ok: true, data: body.data };
    }

    return {
      ok: false,
      error:
        response?.message ||
        response?.error ||
        'Failed to fetch the conflicting Cal.com account',
      status: response?.status,
    };
  } catch (error: any) {
    console.error('Cal.com conflicting integration request failed:', error);
    return {
      ok: false,
      error: error?.message || 'Failed to fetch the conflicting Cal.com account',
    };
  }
};

export const disconnectCalcom = async (): Promise<
  ICalcomApiResult<boolean>
> => {
  try {
    const response: any = await apiReq(
      `${baseUrl}api/calendar/calcom/oauth/disconnect`,
      'DELETE',
      true,
      null
    );

    if (response && response.ok) {
      const body = await response.json();
      return { ok: true, data: body.data };
    }

    return {
      ok: false,
      error:
        response?.message ||
        response?.error ||
        'Failed to disconnect Cal.com account',
      status: response?.status,
    };
  } catch (error: any) {
    console.error('Cal.com OAuth disconnect request failed:', error);
    return {
      ok: false,
      error: error?.message || 'Failed to disconnect Cal.com account',
    };
  }
};

export const getUserAssociatedProperties = async (): Promise<
  ICalcomApiResult<IUserAssociatedProperty[]>
> => {
  try {
    const response: any = await apiReq(
      `${baseUrl}api/calendar/calcom/user-associated-properties`,
      'GET',
      true,
      null
    );

    if (response && response.ok) {
      const body = await response.json();
      return { ok: true, data: body.data };
    }

    return {
      ok: false,
      error:
        response?.message ||
        response?.error ||
        'Failed to fetch your properties',
      status: response?.status,
    };
  } catch (error: any) {
    console.error('Cal.com user-associated-properties request failed:', error);
    return {
      ok: false,
      error: error?.message || 'Failed to fetch your properties',
    };
  }
};

export const getCalcomEventTypes = async (): Promise<
  ICalcomApiResult<ICalcomEventType[]>
> => {
  try {
    const response: any = await apiReq(
      `${baseUrl}api/calendar/calcom/event-types`,
      'GET',
      true,
      null
    );

    if (response && response.ok) {
      const body = await response.json();
      return { ok: true, data: body.data };
    }

    return {
      ok: false,
      error:
        response?.message ||
        response?.error ||
        'Failed to fetch Cal.com event types',
      status: response?.status,
    };
  } catch (error: any) {
    console.error('Cal.com event-types request failed:', error);
    return {
      ok: false,
      error: error?.message || 'Failed to fetch Cal.com event types',
    };
  }
};

export const getPropertyBookingLinks = async (
  propertyId: string,
  kind?: JuniperEventType
): Promise<ICalcomApiResult<IPropertyBookingLink[]>> => {
  try {
    const params: URLSearchParams = new URLSearchParams({ propertyId });

    if (kind !== undefined) {
      params.set('kind', String(kind));
    }

    const response: any = await apiReq(
      `${baseUrl}api/calendar/calcom/property-booking-links?${params.toString()}`,
      'GET',
      true,
      null
    );

    if (response && response.ok) {
      const body = await response.json();
      return { ok: true, data: body.data };
    }

    return {
      ok: false,
      error:
        response?.message ||
        response?.error ||
        'Failed to fetch booking links',
      status: response?.status,
    };
  } catch (error: any) {
    console.error('Cal.com property-booking-links request failed:', error);
    return {
      ok: false,
      error: error?.message || 'Failed to fetch booking links',
    };
  }
};

export const getMyPropertyBookingLinks = async (): Promise<
  ICalcomApiResult<IPropertyBookingLink[]>
> => {
  try {
    const response: any = await apiReq(
      `${baseUrl}api/calendar/calcom/properties-booking-links`,
      'GET',
      true,
      null
    );

    if (response && response.ok) {
      const body = await response.json();
      return { ok: true, data: body.data };
    }

    return {
      ok: false,
      error:
        response?.message ||
        response?.error ||
        'Failed to fetch booking links',
      status: response?.status,
    };
  } catch (error: any) {
    console.error('Cal.com my-property-booking-links request failed:', error);
    return {
      ok: false,
      error: error?.message || 'Failed to fetch booking links',
    };
  }
};

export const getKeyPickupSummaries = async (): Promise<
  ICalcomApiResult<IKeyPickupSummary[]>
> => {
  try {
    const response: any = await apiReq(
      `${baseUrl}api/calendar/calcom/key-pickup/summaries`,
      'GET',
      true,
      null
    );

    if (response && response.ok) {
      const body = await response.json();
      return { ok: true, data: body.data };
    }

    return {
      ok: false,
      error:
        response?.message ||
        response?.error ||
        'Failed to fetch key-pickup summaries',
      status: response?.status,
    };
  } catch (error: any) {
    console.error('Cal.com key-pickup summaries request failed:', error);
    return {
      ok: false,
      error: error?.message || 'Failed to fetch key-pickup summaries',
    };
  }
};

export const getUnitKeyPickupStatuses = async (
  propertyId: string
): Promise<ICalcomApiResult<IUnitKeyPickupStatus[]>> => {
  try {
    const response: any = await apiReq(
      `${baseUrl}api/units/key-pickup-stat/${propertyId}`,
      'GET',
      true,
      null
    );

    if (response && response.ok) {
      const body = await response.json();
      return { ok: true, data: body.data };
    }

    return {
      ok: false,
      error:
        response?.message ||
        response?.error ||
        'Failed to fetch unit key-pickup statuses',
      status: response?.status,
    };
  } catch (error: any) {
    console.error('Unit key-pickup statuses request failed:', error);
    return {
      ok: false,
      error: error?.message || 'Failed to fetch unit key-pickup statuses',
    };
  }
};

export const bulkInviteKeyPickups = async (
  request: IBulkInviteKeyPickupsRequest
): Promise<ICalcomApiResult<unknown>> => {
  try {
    const response: any = await apiReq(
      `${baseUrl}api/calendar/calcom/key-pickup/bulk-invite`,
      'POST',
      true,
      request
    );

    if (response && response.ok) {
      const body = await response.json();
      return { ok: true, data: body.data };
    }

    return {
      ok: false,
      error:
        response?.message ||
        response?.error ||
        'Failed to send key-pickup invites',
      status: response?.status,
    };
  } catch (error: any) {
    console.error('Cal.com key-pickup bulk-invite request failed:', error);
    return {
      ok: false,
      error: error?.message || 'Failed to send key-pickup invites',
    };
  }
};

export const createPropertyBookingLink = async (
  request: ICreatePropertyBookingLinkRequest
): Promise<ICalcomApiResult<IPropertyBookingLink>> => {
  try {
    const response: any = await apiReq(
      `${baseUrl}api/calendar/calcom/property-booking-links`,
      'POST',
      true,
      request
    );

    if (response && response.ok) {
      const body = await response.json();
      return { ok: true, data: body.data };
    }

    return {
      ok: false,
      error:
        response?.message ||
        response?.error ||
        'Failed to create booking link',
      status: response?.status,
    };
  } catch (error: any) {
    console.error('Cal.com create-booking-link request failed:', error);
    return {
      ok: false,
      error: error?.message || 'Failed to create booking link',
    };
  }
};

export const updatePropertyBookingLink = async (
  bookingLinkId: string,
  request: IUpdatePropertyBookingLinkRequest
): Promise<ICalcomApiResult<IPropertyBookingLink>> => {
  try {
    const response: any = await apiReq(
      `${baseUrl}api/calendar/calcom/property-booking-links/${bookingLinkId}`,
      'PUT',
      true,
      request
    );

    if (response && response.ok) {
      const body = await response.json();
      return { ok: true, data: body.data };
    }

    return {
      ok: false,
      error:
        response?.message ||
        response?.error ||
        'Failed to update booking link',
      status: response?.status,
    };
  } catch (error: any) {
    console.error('Cal.com update-booking-link request failed:', error);
    return {
      ok: false,
      error: error?.message || 'Failed to update booking link',
    };
  }
};

export const deletePropertyBookingLink = async (
  bookingLinkId: string
): Promise<ICalcomApiResult<boolean>> => {
  try {
    const response: any = await apiReq(
      `${baseUrl}api/calendar/calcom/property-booking-links/${bookingLinkId}`,
      'DELETE',
      true,
      null
    );

    if (response && response.ok) {
      const body = await response.json();
      return { ok: true, data: body.data };
    }

    return {
      ok: false,
      error:
        response?.message ||
        response?.error ||
        'Failed to remove booking link',
      status: response?.status,
    };
  } catch (error: any) {
    console.error('Cal.com delete-booking-link request failed:', error);
    return {
      ok: false,
      error: error?.message || 'Failed to remove booking link',
    };
  }
};


// Fetches spark deposits for a specific unit/suite.
export const getSparkDeposits = async (
  suiteId: string
): Promise<ISparkDeposit[]> => {
  const url: string = baseUrl + `api/units/${suiteId}/spark-deposits`;

  try {
    const response: any = await apiReq(url, 'GET', true, null);

    if (response && response.ok) {
      const data: ISparkDepositsResponse = await response.json();
      return data.data || [];
    }

    console.error('Unexpected Spark Deposits API error:', response);
    throw new Error(response.error || 'Failed to fetch spark deposits');
  } catch (error: any) {
    console.error('Spark Deposits API exception:', error);
    throw error;
  }
};

// Fetches contract files for a specific unit/suite
export const getContractFiles = async (
  suiteId: string
): Promise<IContractFile[]> => {
  const url: string = baseUrl + `api/units/${suiteId}/contract-files`;

  try {
    const response: any = await apiReq(url, 'GET', true, null);

    if (response && response.ok) {
      const data: IContractFilesResponse = await response.json();
      return data.data || [];
    }

    console.error('Unexpected Contract Files API error:', response);
    throw new Error(response.error || 'Failed to fetch contract files');
  } catch (error: any) {
    console.error('Contract Files API exception:', error);
    throw error;
  }
};

export const bulkActivateWalkthroughsFromAPI = async (requestData: BulkActivateWalkthroughsPayload) => {
  const url: string = baseUrl + 'api/Walkthrough/BulkActivateExistingWalkthroughs';

  try {
    const response: any = await apiReq(url, 'POST', true, requestData);

    const isSuccessful: boolean = response?.ok === true;

    if (!isSuccessful) {
      throw new Error(
        `Bulk activate walkthroughs failed with status ${response?.status ?? 'unknown'}`
      );
    }

    const responseSeralized: any = await response.json();
    return responseSeralized.data;
  } catch (error: any) {
    console.error('Bulk Activate Walkthrough API exception:', error);
    throw error;
  }
}

export const bulkAddWalkthroughsFromAPI = async (
  requestData: IBulkWalkthroughRequest
): Promise<IBulkWalkthroughResponse> => {
  const url: string = baseUrl + 'api/Walkthrough/BulkAddWalkthroughs';

  try {
    const response: any = await apiReq(url, 'POST', true, requestData);

    if (response && response.ok) {
      const data: IBulkWalkthroughResponse = await response.json();
      return data;
    }

    const errorData = await response.json().catch(() => ({}));

    throw new Error(
      errorData.message ||
        response.statusText ||
        'Failed to create bulk walkthroughs'
    );
  } catch (error: any) {
    console.error('Bulk Walkthrough API exception:', error);
    throw error;
  }
};

export const bulkAddKeyPickupFromAPI = async (
  requestData: BulkKeyPickupPayloadToAPI
): Promise<IBulkKeyPickupResponse> => {
  const url: string = baseUrl + 'api/properties/BulkKeyPickup';

  try {
    const response: any = await apiReq(url, 'POST', true, requestData);

    if (response && response.ok) {
      const data: IBulkKeyPickupResponse = await response.json();
      return data;
    }

    const errorData = await response.json().catch(() => ({}));

    throw new Error(
      errorData.message ||
        response.statusText ||
        'Failed to create bulk key pickups'
    );
  } catch (error) {
    console.error('Bulk Walkthrough API exception:', error);
    throw error;
  }
};

export const fetchLatestInventoryUnitsCSV = async (
  propertyId: number,
  dateFormat?: CsvDateFormat
): Promise<string> => {
  const query: string = dateFormat ? `?dateFormat=${dateFormat}` : '';
  const url: string = baseUrl + `api/units/units-state-csv/${propertyId}${query}`;

  try {
    const response: any = await apiReq(url, 'GET', true, null);

    if (response && response.ok) {
      const csvString: string = await response.text();
      return csvString;
    }

    console.error(
      'Unexpected API response when trying to fetch the CSV export:',
      response
    );
    throw new Error(response.error || 'Failed to fetch CSV export');
  } catch (error) {
    console.error('Fetch Latest Inventory Units CSV API exception:', error);
    throw error;
  }
};

export const deleteUnitArea = (unitAreaId) =>
  apiReq(baseUrl + 'api/units/area/' + unitAreaId, 'DELETE', true, null);

// Fetch all preconfigured area types available at the tenant/project level. These are the "common rooms" the user can pick from in the Add Room dialog.
export const fetchAreasLookup = () =>
  apiReq(baseUrl + 'api/units/areas/lookup', 'GET', true, null);

/* Fetch all areas currently configured on a specific unit.
 * Used to cross-reference against the lookup list (to show "already added" indicators). */
export const fetchUnitAreas = (unitId) =>
  apiReq(baseUrl + 'api/units/' + unitId + '/areas', 'GET', true, null);

/* Add a preconfigured area to a unit.
 * Used when the user selects from the tenant-level lookup list. */
export const addUnitArea = (data) =>
  apiReq(baseUrl + 'api/units/area', 'POST', true, data);

/*
 * Add a custom area (type "other") to a unit.
 * Used when the user creates a brand-new room name not in the lookup list. */
export const addUnitAreaOther = (data) =>
  apiReq(baseUrl + 'api/units/area/other', 'POST', true, data);

export const toggleWalkthroughSyncAutomation = (propertyId: string, suspend: boolean) =>
  apiReq(
    baseUrl + `api/Properties/${propertyId}/skip-app-sync-automation/${suspend}`,
    'PATCH',
    true,
    null
  );

export const importContacts = (records: HomeOwnerImportModel[]) =>
  apiReq(baseUrl + 'api/units/import/contacts', 'POST', true, records);

export const getSuiteKeyDates = (suiteId: string) =>
  apiReq(baseUrl + `api/Properties/Units/${suiteId}/dates`, 'GET', true, null);

export const checkWalkthroughsStatus = (walkthroughIds: string[]) =>
  apiReq(
    baseUrl + 'api/homeinspector/walkthroughs-status',
    'POST',
    true,
    walkthroughIds
  );

export const getWalkthroughBackupPresignedUrl = () =>
  apiReq(
    baseUrl + 'api/homeinspector/ps-url?backupSession=true',
    'GET',
    true,
    null
  );

export const fetchRecentOutboxEmails = (skip: number, take: number) =>
  apiReq(
    baseUrl + `api/email-outbox/recent?skip=${skip}&take=${take}`,
    'GET',
    true,
    null
  );

// The browser WebSocket API cannot send custom headers, so the hub takes the
// tenant from the query string instead of the usual Tenant-Id header.
export const getEmailOutboxHubUrl = (): string =>
  baseUrl +
  'hubs/email-outbox?tenant_id=' +
  encodeURIComponent(resolutionHeader);
